import React, { useState, useMemo } from 'react';

const MergeTagPicker = ({ tags, groupedTags, onInsertTag, filters }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCategories, setExpandedCategories] = useState({});
  const [showFilters, setShowFilters] = useState(false);

  // Category display names
  const categoryLabels = {
    company: 'Company',
    event: 'Event',
    user: 'User',
    urls: 'URLs',
    analytics: 'Analytics'
  };

  // Category icons
  const categoryIcons = {
    company: 'building',
    event: 'calendar',
    user: 'user',
    urls: 'link',
    analytics: 'chart-line'
  };

  // Filter tags based on search
  const filteredGroupedTags = useMemo(() => {
    if (!searchTerm.trim()) {
      return groupedTags;
    }

    const term = searchTerm.toLowerCase();
    const filtered = {};

    Object.entries(groupedTags || {}).forEach(([category, categoryTags]) => {
      const matchingTags = categoryTags.filter(tag =>
        tag.key.toLowerCase().includes(term) ||
        tag.description.toLowerCase().includes(term)
      );
      if (matchingTags.length > 0) {
        filtered[category] = matchingTags;
      }
    });

    return filtered;
  }, [groupedTags, searchTerm]);

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const handleInsert = (tagKey) => {
    onInsertTag(`{{${tagKey}}}`);
  };

  const categories = Object.keys(filteredGroupedTags || {});

  return (
    <div className="merge-tag-picker">
      <div className="picker-header">
        <h6 className="picker-title">
          <i className="fa fa-tags mr-2"></i>
          Merge Tags
        </h6>
      </div>

      {/* Search */}
      <div className="picker-search">
        <div className="input-group input-group-sm">
          <div className="input-group-prepend">
            <span className="input-group-text">
              <i className="fa fa-search"></i>
            </span>
          </div>
          <input
            type="text"
            className="form-control"
            placeholder="Search tags..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <div className="input-group-append">
              <button
                className="btn btn-outline-secondary"
                type="button"
                onClick={() => setSearchTerm('')}
              >
                <i className="fa fa-times"></i>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Tag categories */}
      <div className="picker-categories">
        {categories.length === 0 ? (
          <div className="text-muted text-center py-3">
            <small>No tags found</small>
          </div>
        ) : (
          categories.map(category => (
            <div key={category} className="tag-category">
              <button
                className="category-header"
                onClick={() => toggleCategory(category)}
              >
                <span className="category-icon">
                  <i className={`fa fa-${categoryIcons[category] || 'tag'}`}></i>
                </span>
                <span className="category-name">{categoryLabels[category] || category}</span>
                <span className="category-count">
                  {filteredGroupedTags[category]?.length || 0}
                </span>
                <i className={`fa fa-chevron-${expandedCategories[category] ? 'up' : 'down'} ml-auto`}></i>
              </button>

              {expandedCategories[category] && (
                <ul className="tag-list">
                  {filteredGroupedTags[category]?.map(tag => (
                    <li key={tag.key} className="tag-item">
                      <div className="tag-info">
                        <code className="tag-key">{tag.key}</code>
                        <small className="tag-description">{tag.description}</small>
                        {tag.example && (
                          <small className="tag-example">e.g., "{tag.example}"</small>
                        )}
                      </div>
                      <button
                        className="btn btn-sm btn-link tag-insert-btn"
                        onClick={() => handleInsert(tag.key)}
                        title="Click to insert"
                      >
                        <i className="fa fa-plus-circle"></i>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))
        )}
      </div>

      {/* Filters section */}
      {filters && filters.length > 0 && (
        <div className="picker-filters">
          <button
            className="filters-toggle"
            onClick={() => setShowFilters(!showFilters)}
          >
            <i className="fa fa-filter mr-2"></i>
            Available Filters
            <i className={`fa fa-chevron-${showFilters ? 'up' : 'down'} ml-auto`}></i>
          </button>

          {showFilters && (
            <div className="filters-list">
              {filters.map(filter => (
                <div key={filter.name} className="filter-item">
                  <code className="filter-name">{filter.name}</code>
                  <small className="filter-description">{filter.description}</small>
                  <small className="filter-usage">{filter.usage}</small>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <style>{`
        .merge-tag-picker {
          display: flex;
          flex-direction: column;
          height: 100%;
          border: 1px solid #dee2e6;
          border-radius: 4px;
          background: #f8f9fa;
        }

        .picker-header {
          padding: 12px;
          border-bottom: 1px solid #dee2e6;
          background: white;
        }

        .picker-title {
          margin: 0;
          font-size: 14px;
          font-weight: 600;
          color: #495057;
        }

        .picker-search {
          padding: 8px;
          border-bottom: 1px solid #dee2e6;
          background: white;
        }

        .picker-categories {
          flex: 1;
          overflow-y: auto;
          padding: 8px;
        }

        .tag-category {
          margin-bottom: 4px;
        }

        .category-header {
          display: flex;
          align-items: center;
          width: 100%;
          padding: 8px 10px;
          border: none;
          background: white;
          border-radius: 4px;
          cursor: pointer;
          font-size: 13px;
          transition: background-color 0.15s;
        }

        .category-header:hover {
          background: #e9ecef;
        }

        .category-icon {
          width: 24px;
          color: #6c757d;
        }

        .category-name {
          font-weight: 500;
        }

        .category-count {
          margin-left: 8px;
          padding: 2px 6px;
          font-size: 11px;
          background: #e9ecef;
          border-radius: 10px;
          color: #6c757d;
        }

        .tag-list {
          list-style: none;
          padding: 0;
          margin: 4px 0 0 0;
        }

        .tag-item {
          display: flex;
          align-items: flex-start;
          padding: 8px 10px 8px 34px;
          background: white;
          border-radius: 4px;
          margin-bottom: 2px;
        }

        .tag-item:hover {
          background: #f0f9ff;
        }

        .tag-info {
          flex: 1;
          min-width: 0;
        }

        .tag-key {
          display: block;
          font-size: 12px;
          color: #0066cc;
          word-break: break-all;
        }

        .tag-description {
          display: block;
          color: #6c757d;
          font-size: 11px;
          margin-top: 2px;
        }

        .tag-example {
          display: block;
          color: #28a745;
          font-size: 10px;
          font-style: italic;
          margin-top: 2px;
        }

        .tag-insert-btn {
          padding: 0 4px;
          color: #28a745;
          opacity: 0.6;
          transition: opacity 0.15s;
        }

        .tag-insert-btn:hover {
          opacity: 1;
        }

        .picker-filters {
          border-top: 1px solid #dee2e6;
          background: white;
        }

        .filters-toggle {
          display: flex;
          align-items: center;
          width: 100%;
          padding: 10px 12px;
          border: none;
          background: none;
          font-size: 13px;
          color: #6c757d;
          cursor: pointer;
        }

        .filters-toggle:hover {
          background: #f8f9fa;
        }

        .filters-list {
          padding: 8px 12px 12px;
          max-height: 200px;
          overflow-y: auto;
        }

        .filter-item {
          padding: 8px;
          background: #f8f9fa;
          border-radius: 4px;
          margin-bottom: 4px;
        }

        .filter-name {
          font-size: 12px;
          color: #0066cc;
        }

        .filter-description {
          display: block;
          color: #495057;
          font-size: 11px;
          margin-top: 2px;
        }

        .filter-usage {
          display: block;
          color: #6c757d;
          font-size: 10px;
          font-family: monospace;
          margin-top: 4px;
        }
      `}</style>
    </div>
  );
};

export default MergeTagPicker;
